import {formatDate} from '../utils/time'
import * as AbWorkflow from './util/workflow'
import * as DataV2 from './archibus-v2/DataApi'
import * as Document from './archibus/DocumentApi'
import meetingSpaceIcon from '../assets/img/meeting-space-booking-essential.jpg'
import locateIcon from '../assets/img/locate-essential.jpg'
import moveIcon from '../assets/img/move-essential.jpg'
import workspaceIcon from '../assets/img/workspace-booking-essential.jpg'
import * as ReactGA from 'react-ga'
import {_L} from '../context/LocaleContext'
import {fetchBuilding} from '../utils/buildings'

const convertDataURLToData = data => {
  // Find the first comma
  let idx = data.indexOf(',')
  return data.substring(idx)
}

export const submitServiceRequest = async data => {
  const {
    building,
    floor,
    room,
    equipment,
    requester,
    requesterPhone,
    location,
    doc1,
    doc2,
    doc3,
    doc4,
    dv_id,
    dp_id,
    probType,
    activityType
  } = data

  let {siteId, description} = data

  if ((siteId === null || siteId === '') && building.value) {
    const {siteId: siteByBuilding} = await fetchBuilding({blId: building.value})
    siteId = siteByBuilding
  }

  let parameters = {
    'activity_log.site_id': siteId,
    'activity_log.bl_id': building.value,
    'activity_log.fl_id': floor.value,
    'activity_log.rm_id': room.value,
    'activity_log.eq_id': equipment.value,
    'activity_log.dv_id': dv_id,
    'activity_log.dp_id': dp_id,
    'activity_log.requestor': requester,
    'activity_log.phone_requestor': requesterPhone,
    'activity_log.date_requested': formatDate(new Date(), 'YYYY-MM-DD'),
    'activity_log.location': location
  }

  if (probType) {
    const {title} = await fetchActivityType(activityType)
    parameters['activity_log.prob_type'] = probType
    // DO NOT CHANGE THE ACTIVITY_TYPE SERVICE DESK - MAINTENANCE
    parameters['activity_log.activity_type'] = 'SERVICE DESK - MAINTENANCE'
    description = description + ' (' + title + ')'
  } else {
    parameters['activity_log.activity_type'] =
      // activityType is default 'SERVICE DESK - MAINTENANCE' if it isn't defined
      activityType || 'SERVICE DESK - MAINTENANCE'
  }
  parameters['activity_log.description'] = description

  let activity_log_id = 0

  // TODO: File name is always png
  let documents = [doc1, doc2, doc3, doc4].reduce((acc, item, idx) => {
    if (item) {
      acc.push({
        data: convertDataURLToData(item),
        field: `doc${idx + 1}`,
        fileName: `doc${idx + 1}.png`,
        table: 'activity_log'
      })
    }
    return acc
  }, [])

  const {data: requestResult, code} = await AbWorkflow.callMethod(
    'AbBldgOpsHelpDesk-RequestsService-saveRequest',
    [parameters]
  )

  if (code === 'executed') {
    activity_log_id = requestResult.activity_log_id.toString()
    let key = {
      activity_log_id
    }

    await Promise.all(
      documents.map(({data, table, field, fileName}) => {
        return AbWorkflow.callMethod(
          'AbCommonResources-DocumentManager-uploadDocument',
          [data, table, field, key, fileName]
        )
      })
    )

    parameters['activity_log.activity_log_id'] = parseInt(activity_log_id)

    await AbWorkflow.callMethod(
      'AbBldgOpsHelpDesk-RequestsService-submitRequest',
      [activity_log_id, parameters]
    )

    ReactGA.event({
      category: 'Service Request',
      action: 'Create Service Request'
    })
  }
}

const fetchServiceRequests = ({restriction}) => {
  let requests = []
  return DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'serviceRequests',
    restriction
  )
    .then(result => {
      let {data} = result
      requests = data

      let employeePhotos = data.reduce((acc, item) => {
        if (item.emEmPhoto) {
          acc.push({
            tableName: 'em',
            keys: {em_id: item.requestor},
            docField: 'em_photo'
          })
        }
        return acc
      }, [])

      return Promise.all(
        employeePhotos.map(emId => {
          return getDocument(emId)
        })
      )
    })
    .then(urls => {
      let employeeUrls = urls.reduce((acc, item) => {
        if (!acc[item.em_id]) {
          acc[item.em_id] = item.url
        }
        return acc
      }, {})

      let serviceRequests = requests.map(request => {
        request.imageUrl = null
        if (employeeUrls[request.requestor]) {
          request.imageUrl = encodeURI(employeeUrls[request.requestor])
        }
        return request
      })

      return Promise.resolve(serviceRequests)
    })
}

export const fetchServiceRequestsForEquipment = ({eqId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('activity_log.eq_id', eqId)
  return fetchServiceRequests({restriction})
}

export const fetchServiceRequestsForRoom = ({blId, flId, rmId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('activity_log.bl_id', blId)
  restriction.addClause('activity_log.fl_id', flId)
  restriction.addClause('activity_log.rm_id', rmId)

  return fetchServiceRequests({restriction})
}

const fetchServiceRequest = ({restriction}) => {
  let request = {}
  return DataV2.getDataFromDataSource(
    'ab-selfservice-dashboard.axvw',
    'serviceRequests',
    restriction
  )
    .then(async result => {
      let {data} = result
      request = data[0]
      const {activityLogId, probType, doc1, doc2, doc3, doc4} = data[0]

      let probTypeDescription = ''
      if (probType) {
        const {description} = await fetchProblemType(probType)
        probTypeDescription = description
      }
      request['probTypeDescription'] = probTypeDescription

      let documents = [doc1, doc2, doc3, doc4].reduce((acc, item, idx) => {
        if (item) {
          acc.push({
            tableName: 'activity_log',
            keys: {activity_log_id: activityLogId},
            docField: `doc${idx + 1}`
          })
        }
        return acc
      }, [])

      return Promise.all(
        documents.map(doc => {
          return getDocument(doc)
        })
      )
    })
    .then(urls => {
      let documentsUrls = urls.reduce((acc, item) => {
        if (!acc[item.docField]) {
          const key = item.docField + 'Url'
          acc[key] = item.url
        }
        return acc
      }, {})

      Object.keys(documentsUrls).forEach(document => {
        request[document] = encodeURI(documentsUrls[document])
      })

      return Promise.resolve(request)
    })
}

export const fetchServiceRequestDetails = ({activityLogId}) => {
  let restriction = new Ab.view.Restriction()
  restriction.addClause('activity_log.activity_log_id', activityLogId)
  return fetchServiceRequest({restriction})
}

const getDocument = ({tableName, keys, docField}) => {
  return Document.getTempDocumentUrl(tableName, keys, docField).then(url => {
    return Promise.resolve({url, ...keys, docField})
  })
}

const getStaticCatalogItems = ({appParameters, localeState}) => {
  let items = {}

  const {workingspace, meetingspace, search, moverequest} = appParameters

  // Meeting Space
  if (meetingspace) {
    items['MEETING_SPACE'] = {
      title: _L({key: 'MEETING SPACE BOOKING', localeState}),
      imageUrl: meetingSpaceIcon,
      ids: ['MEETING_SPACE'],
      displayOrder: 1,
      children: {},
      activityType: 'MEETING_SPACE',
      isStatic: true
    }
  }

  // Workspace
  if (workingspace) {
    items['WORKSPACE'] = {
      title: _L({key: 'WORKSPACE BOOKING', localeState}),
      imageUrl: workspaceIcon,
      ids: ['WORKSPACE'],
      displayOrder: 2,
      children: {},
      activityType: 'WORKSPACE',
      isStatic: true
    }
  }

  // Locate
  if (search) {
    items['LOCATE'] = {
      title: _L({key: 'LOCATE A PERSON OR ROOM', localeState}),
      imageUrl: locateIcon,
      ids: ['KIOSK_CONTAINER'],
      displayOrder: 3,
      children: {},
      activityType: 'LOCATE',
      isStatic: true
    }
  }

  // Move
  if (moverequest) {
    items['MOVE'] = {
      title: _L({key: 'MOVE REQUEST', localeState}),
      imageUrl: moveIcon,
      ids: ['MOVE'],
      displayOrder: 4,
      children: {},
      activityType: 'MOVE',
      isStatic: true
    }
  }

  return items
}

export const fetchActivityType = activityType => {
  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'serviceTypes',
    new Ab.view.Restriction({'activitytype.activity_type': activityType})
  ).then(result => {
    const {data} = result
    let [activityType] = data
    return Promise.resolve(activityType)
  })
}

export const fetchProblemType = probType => {
  return DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'problemType',
    new Ab.view.Restriction({'probtype.prob_type': probType})
  ).then(result => {
    const {data} = result
    let [probType] = data
    return Promise.resolve(probType)
  })
}

/**
 * Fetches the Activity Types used to generate the Service Catalog list
 * @param {Boolean} includeActions Includes the actions for Move Requests,
 * @param {Object} appParameters Application parameters used to control item visibilty in the UI
 * @param {Object} localeState Contains the localization state context.
 * Workspace Booking, Meeting Reservations and Locate.
 * @returns {Promise<{data: *, descriptions: *}>}
 */
export const fetchServiceCatalogTypes = async ({
  includeActions,
  appParameters,
  localeState
}) => {
  const {data} = await DataV2.getDataFromDataSource(
    'ab-self-service.axvw',
    'serviceTypes'
  )
  // Get images
  let imageRecords = data.filter(({menuIcon}) => !!menuIcon)

  let imageUrls = await Promise.all(
    imageRecords.map(async ({activityType}) => {
      let encodedImageUrl = null

      try {
        const imageUrl = await Document.getTempDocumentUrl(
          'activitytype',
          {activity_type: activityType},
          'menu_icon'
        )
        encodedImageUrl = encodeURI(imageUrl)
      } catch (error) {
        console.log('Document Error: ', error)
      }

      return {
        activityType,
        encodedImageUrl
      }
    })
  )

  let imageUrlMap = imageUrls.reduce((acc, {activityType, encodedImageUrl}) => {
    acc[activityType] = encodedImageUrl
    return acc
  }, {})

  // Process hierarchy

  let dataMap = data
    .map(record => {
      const {hierarchyIds, activityType} = record
      const ids = hierarchyIds.split('|')
      record.level = ids.length - 1
      record.ids = ids.filter(x => x !== '')
      record.imageUrl = null
      // Add the image if it exists
      if (imageUrlMap[activityType]) {
        record.imageUrl = imageUrlMap[activityType]
      }

      return record
    })
    .sort((a, b) => a.level - b.level)

  // Map the description to the activityType key. Provides an easy
  // way to look up the description
  let mappedDescriptions = dataMap.reduce((acc, item) => {
    const {activityType, title} = item
    acc[activityType] = title
    return acc
  }, {})

  let mappedObject = dataMap.reduce((acc, item) => {
    const {activityType, ids, level} = item

    if (level === 1) {
      item.children = {}
      acc[activityType] = item
    }

    if (level === 2) {
      const [key1, key2] = ids
      item.children = {}
      if (acc[key1] && acc[key1].children) {
        acc[key1].children[key2] = item
      }
    }

    if (level === 3) {
      const [key1, key2, key3] = ids
      item.children = {}
      if (
        acc[key1] &&
        acc[key1].children &&
        acc[key1].children[key2] &&
        acc[key1].children[key2].children
      ) {
        acc[key1].children[key2].children[key3] = item
      }
    }

    return acc
  }, {})

  let staticItems = includeActions
    ? getStaticCatalogItems({appParameters, localeState})
    : {}

  return {
    data: {...staticItems, ...mappedObject},
    descriptions: mappedDescriptions
  }
}
